import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Moon,
  Rocket,
  Users,
  Calendar,
  Video,
  MapPin,
  Sparkles,
  Heart,
  Settings,
  Loader2,
  CheckCircle2,
  Globe,
  Target,
  Accessibility
} from "lucide-react";

const TEMPLATE_OPTIONS = [
  {
    key: "community_events",
    name: "Community",
    subtitle: "Community Events",
    description: "For workshops, experiences, and creator meetups. Supports both IRL and URL events.",
    icon: Moon,
    color: "#8b5cf6",
    bgColor: "bg-violet-500",
    lightBg: "bg-violet-50",
    borderColor: "border-violet-200",
    features: [
      "Audience types: Student, Founder, Creator, Developer",
      "Interests: AI Tools, Creative Technology, Community",
      "Full accessibility support",
      "Dietary preferences for IRL events"
    ],
    eventType: "hybrid",
    orgName: "Phygital Community"
  },
  {
    key: "builder_host",
    name: "Builder",
    subtitle: "Builder Events",
    description: "Host profile for builder-focused events, workshops, and product sessions.",
    icon: Rocket,
    color: "#3b82f6",
    bgColor: "bg-blue-500",
    lightBg: "bg-blue-50",
    borderColor: "border-blue-200",
    features: [
      "Builder stages: Pre-idea to Scaling",
      "Industries: AI/ML, FinTech, HealthTech, etc.",
      "Topics: Fundraising, Growth, Product Strategy",
      "Zoom integration for virtual events"
    ],
    eventType: "url",
    orgName: "Phygital Builders"
  }
];

const STEPS = [
  { id: "template", title: "Choose Template", icon: Sparkles },
  { id: "profile", title: "Host Profile", icon: Users },
  { id: "customize", title: "Customize Fields", icon: Settings },
  { id: "event", title: "First Event", icon: Calendar },
  { id: "review", title: "Review & Create", icon: Check }
];

export default function InstructorPilotWizard({ onComplete, onCancel }) {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  
  // Form state
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [profileData, setProfileData] = useState({
    name: "",
    description: "",
    host_name: "",
    host_bio: "",
    host_title: "",
    host_socials: {
      instagram: "",
      linkedin: "",
      website: ""
    }
  });
  
  const [customFields, setCustomFields] = useState({
    audience_types: [],
    interests: [],
    accessibility_options: []
  });
  
  const [createFirstEvent, setCreateFirstEvent] = useState(true);
  const [eventData, setEventData] = useState({
    name: "",
    description: "",
    event_type: "url",
    date: ""
  });
  
  // Load template defaults when template is selected
  useEffect(() => {
    if (selectedTemplate) {
      const template = TEMPLATE_OPTIONS.find(t => t.key === selectedTemplate);
      if (template) {
        setEventData(prev => ({
          ...prev,
          event_type: template.eventType
        }));
        
        // Fetch full template config
        fetchTemplateConfig(selectedTemplate);
      }
    }
  }, [selectedTemplate]);
  
  const fetchTemplateConfig = async (templateKey) => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/instructor-pilots/templates/tpl_${templateKey}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        const template = data.template;
        
        setCustomFields({
          audience_types: template.audience_types || [],
          interests: template.interests || [],
          builder_types: template.builder_types || [],
          industries: template.industries || [],
          topics_of_interest: template.topics_of_interest || [],
          accessibility_options: template.accessibility_options || []
        });
      }
    } catch (err) {
      console.error("Failed to fetch template:", err);
    }
  };
  
  const handleNext = () => {
    if (currentStep === 0 && !selectedTemplate) {
      toast.error("Please select a template");
      return;
    }
    
    if (currentStep === 1) {
      if (!profileData.name) {
        toast.error("Please enter a profile name");
        return;
      }
      if (!profileData.host_name) {
        toast.error("Please enter the host name");
        return;
      }
    }
    
    if (currentStep === 3 && createFirstEvent) {
      if (!eventData.name) {
        toast.error("Please enter an event name");
        return;
      }
    }
    
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };
  
  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };
  
  const handleCreate = async () => {
    setCreating(true);
    
    try {
      const token = localStorage.getItem("session_token");
      
      const payload = {
        template: selectedTemplate,
        name: profileData.name,
        description: profileData.description,
        host_name: profileData.host_name,
        host_bio: profileData.host_bio,
        host_title: profileData.host_title,
        host_socials: profileData.host_socials,
        create_first_event: createFirstEvent,
        ...(createFirstEvent && {
          first_event: {
            name: eventData.name,
            description: eventData.description,
            event_type: eventData.event_type,
            date: eventData.date || null
          }
        })
      };
      
      const res = await fetch(`${API}/instructor-pilots/quick-setup`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || "Failed to create pilot");
      }
      
      const result = await res.json();
      
      toast.success("Instructor pilot created successfully!");
      
      if (onComplete) {
        onComplete(result);
      } else {
        // Navigate to the new profile
        navigate(`/admin/instructor-pilots/${result.profile.profile_id}`);
      }
    } catch (err) {
      console.error("Failed to create pilot:", err);
      toast.error(err.message || "Failed to create pilot");
    } finally {
      setCreating(false);
    }
  };
  
  const toggleFieldEnabled = (category, fieldId) => {
    setCustomFields(prev => ({
      ...prev,
      [category]: prev[category].map(f => 
        f.id === fieldId ? { ...f, enabled: !f.enabled } : f
      )
    }));
  };
  
  const selectedTemplateData = TEMPLATE_OPTIONS.find(t => t.key === selectedTemplate);
  
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <Button variant="ghost" onClick={onCancel} className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Cancel
          </Button>
          <h1 className="text-3xl font-bold text-gray-900">Create Instructor Pilot</h1>
          <p className="text-gray-600 mt-2">Set up a host profile for managing events with shared audience insights</p>
        </div>
        
        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {STEPS.map((step, idx) => {
              const StepIcon = step.icon;
              const isActive = idx === currentStep;
              const isComplete = idx < currentStep;
              
              return (
                <div key={step.id} className="flex items-center">
                  <div className={`
                    flex items-center justify-center w-10 h-10 rounded-full
                    ${isComplete ? "bg-green-500 text-white" : ""}
                    ${isActive ? "bg-violet-600 text-white" : ""}
                    ${!isActive && !isComplete ? "bg-gray-200 text-gray-500" : ""}
                  `}>
                    {isComplete ? (
                      <CheckCircle2 className="w-5 h-5" />
                    ) : (
                      <StepIcon className="w-5 h-5" />
                    )}
                  </div>
                  <span className={`ml-2 text-sm font-medium ${isActive ? "text-gray-900" : "text-gray-500"}`}>
                    {step.title}
                  </span>
                  {idx < STEPS.length - 1 && (
                    <div className={`w-12 h-0.5 mx-4 ${isComplete ? "bg-green-500" : "bg-gray-200"}`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
        
        {/* Step Content */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            {/* Step 1: Choose Template */}
            {currentStep === 0 && (
              <div className="space-y-6">
                <div className="text-center mb-8">
                  <h2 className="text-2xl font-bold text-gray-900">Choose a Template</h2>
                  <p className="text-gray-600 mt-2">Select a starting point for your instructor pilot</p>
                </div>
                
                <div className="grid md:grid-cols-2 gap-6">
                  {TEMPLATE_OPTIONS.map((template) => {
                    const Icon = template.icon;
                    const isSelected = selectedTemplate === template.key;
                    
                    return (
                      <Card
                        key={template.key}
                        className={`cursor-pointer transition-all hover:shadow-lg ${
                          isSelected ? `ring-2 ring-offset-2 ${template.borderColor.replace("border", "ring")}` : ""
                        }`}
                        onClick={() => setSelectedTemplate(template.key)}
                        data-testid={`template-${template.key}`}
                      >
                        <CardContent className="pt-6">
                          <div className="flex items-start gap-4">
                            <div className={`w-14 h-14 rounded-2xl ${template.bgColor} flex items-center justify-center flex-shrink-0`}>
                              <Icon className="w-7 h-7 text-white" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <h3 className="font-bold text-lg text-gray-900">{template.name}</h3>
                                {isSelected && <CheckCircle2 className="w-5 h-5 text-green-500" />}
                              </div>
                              <Badge variant="secondary" className={`${template.lightBg} mt-1`}>
                                {template.subtitle}
                              </Badge>
                              <p className="text-sm text-gray-600 mt-3">{template.description}</p>
                              <ul className="mt-4 space-y-1">
                                {template.features.map((feature, idx) => (
                                  <li key={idx} className="text-xs text-gray-500 flex items-center gap-2">
                                    <Check className="w-3 h-3 text-green-500" />
                                    {feature}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* Step 2: Host Profile */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <div className="text-center mb-8">
                  <h2 className="text-2xl font-bold text-gray-900">Host Profile</h2>
                  <p className="text-gray-600 mt-2">Tell us about you and your community</p>
                </div>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="profile-name">Profile Name *</Label>
                      <Input
                        id="profile-name"
                        placeholder="e.g., Community Meetups"
                        value={profileData.name}
                        onChange={(e) => setProfileData(prev => ({ ...prev, name: e.target.value }))}
                        data-testid="profile-name-input"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="profile-desc">Description</Label>
                      <Textarea
                        id="profile-desc"
                        placeholder="Describe what this pilot is about..."
                        value={profileData.description}
                        onChange={(e) => setProfileData(prev => ({ ...prev, description: e.target.value }))}
                        rows={3}
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="host-name">Host Name *</Label>
                      <Input
                        id="host-name"
                        placeholder="Your name"
                        value={profileData.host_name}
                        onChange={(e) => setProfileData(prev => ({ ...prev, host_name: e.target.value }))}
                        data-testid="host-name-input"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="host-title">Title</Label>
                      <Input
                        id="host-title"
                        placeholder="e.g., Founder, Instructor"
                        value={profileData.host_title}
                        onChange={(e) => setProfileData(prev => ({ ...prev, host_title: e.target.value }))}
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="host-bio">Bio</Label>
                      <Textarea
                        id="host-bio"
                        placeholder="A brief bio for attendees..."
                        value={profileData.host_bio}
                        onChange={(e) => setProfileData(prev => ({ ...prev, host_bio: e.target.value }))}
                        rows={3}
                      />
                    </div>
                  </div>
                </div>
                
                <div className="border-t pt-6">
                  <h3 className="font-medium text-gray-900 mb-4">Social Links</h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="social-instagram">Instagram</Label>
                      <Input
                        id="social-instagram"
                        placeholder="@handle"
                        value={profileData.host_socials.instagram}
                        onChange={(e) => setProfileData(prev => ({
                          ...prev,
                          host_socials: { ...prev.host_socials, instagram: e.target.value }
                        }))}
                      />
                    </div>
                    <div>
                      <Label htmlFor="social-linkedin">LinkedIn</Label>
                      <Input
                        id="social-linkedin"
                        placeholder="linkedin.com/in/..."
                        value={profileData.host_socials.linkedin}
                        onChange={(e) => setProfileData(prev => ({
                          ...prev,
                          host_socials: { ...prev.host_socials, linkedin: e.target.value }
                        }))}
                      />
                    </div>
                    <div>
                      <Label htmlFor="social-website">Website</Label>
                      <Input
                        id="social-website"
                        placeholder="yourwebsite.com"
                        value={profileData.host_socials.website}
                        onChange={(e) => setProfileData(prev => ({
                          ...prev,
                          host_socials: { ...prev.host_socials, website: e.target.value }
                        }))}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Step 3: Customize Fields */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <div className="text-center mb-8">
                  <h2 className="text-2xl font-bold text-gray-900">Customize Fields</h2>
                  <p className="text-gray-600 mt-2">Choose what insights to collect from attendees</p>
                </div>
                
                <div className="space-y-6">
                  {/* Audience Types */}
                  {customFields.audience_types?.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <Users className="w-5 h-5 text-violet-600" />
                        <h3 className="font-medium text-gray-900">Audience Types</h3>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {customFields.audience_types.map((field) => (
                          <Badge
                            key={field.id}
                            variant={field.enabled ? "default" : "outline"}
                            className={`cursor-pointer ${field.enabled ? "bg-violet-600" : ""}`}
                            onClick={() => toggleFieldEnabled("audience_types", field.id)}
                          >
                            {field.enabled && <Check className="w-3 h-3 mr-1" />}
                            {field.label}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Interests */}
                  {customFields.interests?.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <Heart className="w-5 h-5 text-pink-600" />
                        <h3 className="font-medium text-gray-900">Interests</h3>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {customFields.interests.map((field) => (
                          <Badge
                            key={field.id}
                            variant={field.enabled ? "default" : "outline"}
                            className={`cursor-pointer ${field.enabled ? "bg-pink-600" : ""}`}
                            onClick={() => toggleFieldEnabled("interests", field.id)}
                          >
                            {field.enabled && <Check className="w-3 h-3 mr-1" />}
                            {field.label}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Builder Types (Emergent) */}
                  {customFields.builder_types?.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <Target className="w-5 h-5 text-blue-600" />
                        <h3 className="font-medium text-gray-900">Builder Types</h3>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {customFields.builder_types.map((field) => (
                          <Badge
                            key={field.id}
                            variant={field.enabled ? "default" : "outline"}
                            className={`cursor-pointer ${field.enabled ? "bg-blue-600" : ""}`}
                            onClick={() => toggleFieldEnabled("builder_types", field.id)}
                          >
                            {field.enabled && <Check className="w-3 h-3 mr-1" />}
                            {field.label}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Industries (Emergent) */}
                  {customFields.industries?.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <Globe className="w-5 h-5 text-emerald-600" />
                        <h3 className="font-medium text-gray-900">Industries</h3>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {customFields.industries.map((field) => (
                          <Badge
                            key={field.id}
                            variant={field.enabled ? "default" : "outline"}
                            className={`cursor-pointer ${field.enabled ? "bg-emerald-600" : ""}`}
                            onClick={() => toggleFieldEnabled("industries", field.id)}
                          >
                            {field.enabled && <Check className="w-3 h-3 mr-1" />}
                            {field.label}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Accessibility */}
                  {customFields.accessibility_options?.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <Accessibility className="w-5 h-5 text-emerald-600" />
                        <h3 className="font-medium text-gray-900">Accessibility Options</h3>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {customFields.accessibility_options.map((field) => (
                          <Badge
                            key={field.id}
                            variant={field.enabled ? "default" : "outline"}
                            className={`cursor-pointer ${field.enabled ? "bg-emerald-600" : ""}`}
                            onClick={() => toggleFieldEnabled("accessibility_options", field.id)}
                          >
                            {field.enabled && <Check className="w-3 h-3 mr-1" />}
                            {field.label}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Step 4: First Event */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <div className="text-center mb-8">
                  <h2 className="text-2xl font-bold text-gray-900">First Event</h2>
                  <p className="text-gray-600 mt-2">Optionally create your first event</p>
                </div>
                
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg mb-6">
                  <div>
                    <p className="font-medium text-gray-900">Create a first event?</p>
                    <p className="text-sm text-gray-600">You can always create events later</p>
                  </div>
                  <Switch
                    checked={createFirstEvent}
                    onCheckedChange={setCreateFirstEvent}
                    data-testid="create-event-toggle"
                  />
                </div>
                
                {createFirstEvent && (
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="event-name">Event Name *</Label>
                      <Input
                        id="event-name"
                        placeholder="e.g., Front Row Workshop"
                        value={eventData.name}
                        onChange={(e) => setEventData(prev => ({ ...prev, name: e.target.value }))}
                        data-testid="event-name-input"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="event-desc">Description</Label>
                      <Textarea
                        id="event-desc"
                        placeholder="What's this event about?"
                        value={eventData.description}
                        onChange={(e) => setEventData(prev => ({ ...prev, description: e.target.value }))}
                        rows={3}
                      />
                    </div>
                    
                    <div>
                      <Label>Experience Type</Label>
                      <div className="flex gap-4 mt-2">
                        <Button
                          variant={eventData.event_type === "url" ? "default" : "outline"}
                          onClick={() => setEventData(prev => ({ ...prev, event_type: "url" }))}
                          className={eventData.event_type === "url" ? "bg-blue-600" : ""}
                        >
                          <Video className="w-4 h-4 mr-2" />
                          URL (Virtual)
                        </Button>
                        <Button
                          variant={eventData.event_type === "irl" ? "default" : "outline"}
                          onClick={() => setEventData(prev => ({ ...prev, event_type: "irl" }))}
                          className={eventData.event_type === "irl" ? "bg-emerald-600" : ""}
                        >
                          <MapPin className="w-4 h-4 mr-2" />
                          IRL (In-Person)
                        </Button>
                        <Button
                          variant={eventData.event_type === "hybrid" ? "default" : "outline"}
                          onClick={() => setEventData(prev => ({ ...prev, event_type: "hybrid" }))}
                          className={eventData.event_type === "hybrid" ? "bg-violet-600" : ""}
                        >
                          <Globe className="w-4 h-4 mr-2" />
                          Hybrid
                        </Button>
                      </div>
                    </div>
                    
                    <div>
                      <Label htmlFor="event-date">Date (optional)</Label>
                      <Input
                        id="event-date"
                        type="date"
                        value={eventData.date}
                        onChange={(e) => setEventData(prev => ({ ...prev, date: e.target.value }))}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Step 5: Review */}
            {currentStep === 4 && (
              <div className="space-y-6">
                <div className="text-center mb-8">
                  <h2 className="text-2xl font-bold text-gray-900">Review & Create</h2>
                  <p className="text-gray-600 mt-2">Confirm your instructor pilot setup</p>
                </div>
                
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Template */}
                  <Card className={selectedTemplateData?.lightBg}>
                    <CardContent className="pt-4">
                      <div className="flex items-center gap-3 mb-3">
                        {selectedTemplateData && (
                          <div className={`w-10 h-10 rounded-lg ${selectedTemplateData.bgColor} flex items-center justify-center`}>
                            <selectedTemplateData.icon className="w-5 h-5 text-white" />
                          </div>
                        )}
                        <div>
                          <p className="text-sm text-gray-500">Template</p>
                          <p className="font-medium">{selectedTemplateData?.name}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  {/* Profile */}
                  <Card>
                    <CardContent className="pt-4">
                      <p className="text-sm text-gray-500">Profile</p>
                      <p className="font-medium">{profileData.name}</p>
                      <p className="text-sm text-gray-600">Host: {profileData.host_name}</p>
                    </CardContent>
                  </Card>
                  
                  {/* Fields */}
                  <Card>
                    <CardContent className="pt-4">
                      <p className="text-sm text-gray-500 mb-2">Active Fields</p>
                      <div className="flex flex-wrap gap-1">
                        {customFields.audience_types?.filter(f => f.enabled).length > 0 && (
                          <Badge variant="secondary" className="text-xs">
                            {customFields.audience_types.filter(f => f.enabled).length} audience types
                          </Badge>
                        )}
                        {customFields.interests?.filter(f => f.enabled).length > 0 && (
                          <Badge variant="secondary" className="text-xs">
                            {customFields.interests.filter(f => f.enabled).length} interests
                          </Badge>
                        )}
                        {customFields.accessibility_options?.filter(f => f.enabled).length > 0 && (
                          <Badge variant="secondary" className="text-xs">
                            {customFields.accessibility_options.filter(f => f.enabled).length} accessibility
                          </Badge>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                  
                  {/* Event */}
                  <Card>
                    <CardContent className="pt-4">
                      <p className="text-sm text-gray-500">First Event</p>
                      {createFirstEvent ? (
                        <>
                          <p className="font-medium">{eventData.name || "Untitled Event"}</p>
                          <Badge variant="outline" className="mt-1">
                            {eventData.event_type === "url" ? "Virtual" : eventData.event_type === "irl" ? "In-Person" : "Hybrid"}
                          </Badge>
                        </>
                      ) : (
                        <p className="text-gray-400">None (create later)</p>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Navigation */}
        <div className="flex justify-between">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={currentStep === 0}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          
          {currentStep < STEPS.length - 1 ? (
            <Button onClick={handleNext} data-testid="next-btn">
              Next
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleCreate}
              disabled={creating}
              className="bg-green-600 hover:bg-green-700"
              data-testid="create-pilot-btn"
            >
              {creating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Create Instructor Pilot
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
